var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useRef, useState, useMemo } from "react";
import { RadixNavbar } from "../primitives/RadixNavbar";
import "./appHeader.css";
import { useAuth, useCart, useUI } from "../../providers";
import { RadixButton } from "../primitives/RadixButton";
import { ExitIcon, MagnifyingGlassIcon, PersonIcon, PlusIcon } from "@radix-ui/react-icons";
import { RadixInput } from "../primitives/RadixInput";
import { CartIcon, ThemeIcon, UserIcon } from "../icons";
import { ItemCustomizationDialog } from "../itemCustomization/ItemCustomizationDialog";
import GoogleAnalytics from "../../analytics/google";
import { shouldDisplayMenuEntity } from "../utility/menuItemVisibility";
var CATEGORY_HASH_PREFIX = "#category-";
var MAX_SEARCH_RESULTS = 10;
var PENDING_ITEM_KEY = "app-header-pending-item-selection";
var addIndexEntry = function (index, token, entityIndex) {
    var bucket = index.get(token);
    if (!bucket) {
        index.set(token, [entityIndex]);
        return;
    }
    var lastIndex = bucket[bucket.length - 1];
    if (lastIndex !== entityIndex) {
        bucket.push(entityIndex);
    }
};
var indexLabelNgrams = function (label, entityIndex, indexes) {
    var normalizedLabel = label.toLowerCase();
    for (var index = 0; index < normalizedLabel.length; index += 1) {
        var unigram = normalizedLabel.slice(index, index + 1);
        if (unigram.trim()) {
            addIndexEntry(indexes.unigramIndex, unigram, entityIndex);
        }
        var bigram = normalizedLabel.slice(index, index + 2);
        if (bigram.length === 2 && bigram.trim()) {
            addIndexEntry(indexes.bigramIndex, bigram, entityIndex);
        }
        var trigram = normalizedLabel.slice(index, index + 3);
        if (trigram.length === 3 && trigram.trim()) {
            addIndexEntry(indexes.trigramIndex, trigram, entityIndex);
        }
    }
};
var intersectSortedArrays = function (left, right) {
    var result = [];
    var leftPointer = 0;
    var rightPointer = 0;
    while (leftPointer < left.length && rightPointer < right.length) {
        var leftValue = left[leftPointer];
        var rightValue = right[rightPointer];
        if (leftValue === rightValue) {
            result.push(leftValue);
            leftPointer += 1;
            rightPointer += 1;
            continue;
        }
        if (leftValue < rightValue) {
            leftPointer += 1;
            continue;
        }
        rightPointer += 1;
    }
    return result;
};
var isMenuPagePath = function (pathname) {
    var normalizedPath = pathname.toLowerCase();
    var pathSegments = normalizedPath.split("/").filter(Boolean);
    var lastSegment = pathSegments[pathSegments.length - 1];
    return lastSegment === "menu";
};
export var AppHeader = function (_a) {
    var _b, _c, _d;
    var companyName = _a.companyName, logoSrc = _a.logoSrc, logoAlt = _a.logoAlt, _e = _a.homeHref, homeHref = _e === void 0 ? "#" : _e, cartCount = _a.cartCount, onCartClick = _a.onCartClick, _f = _a.sticky, sticky = _f === void 0 ? true : _f, _g = _a.stickyTop, stickyTop = _g === void 0 ? 0 : _g, onSearchSubmit = _a.onSearchSubmit;
    var brandLabel = logoAlt || "".concat(companyName, " home");
    var storeLogoOverride = typeof window === "undefined"
        ? ""
        : String((_d = (_c = (_b = window.session) === null || _b === void 0 ? void 0 : _b.store) === null || _c === void 0 ? void 0 : _c.logo_url_override) !== null && _d !== void 0 ? _d : "").trim();
    var resolvedLogoSrc = storeLogoOverride || logoSrc;
    var items = useCart().items;
    var _h = useUI(), openCart = _h.openCart, openSignInModal = _h.openSignInModal, openSignUpModal = _h.openSignUpModal, toggleDarkMode = _h.toggleDarkMode, effectiveColorMode = _h.effectiveColorMode;
    var cartLineCount = Object.values(items).reduce(function (a, b) { return a + b.quantity; }, 0);
    var _j = useAuth(), isAuthenticated = _j.isAuthenticated, user = _j.user;
    var _k = useState(false), isUserMenuOpen = _k[0], setIsUserMenuOpen = _k[1];
    var _l = useState(""), searchTerm = _l[0], setSearchTerm = _l[1];
    var _m = useState(new Map()), searchMap = _m[0], setSearchMap = _m[1];
    var _o = useState([]), searchEntities = _o[0], setSearchEntities = _o[1];
    var _p = useState({
        unigramIndex: new Map(),
        bigramIndex: new Map(),
        trigramIndex: new Map(),
    }), searchIndexes = _p[0], setSearchIndexes = _p[1];
    var _q = useState(false), isMobileSearchOpen = _q[0], setIsMobileSearchOpen = _q[1];
    var _r = useState(null), itemCustomizationTarget = _r[0], setItemCustomizationTarget = _r[1];
    var userMenuRef = useRef(null);
    var mobileSearchRef = useRef(null);
    var headerRef = useRef(null);
    var readPendingItemSelection = function () {
        if (typeof window === "undefined") {
            return null;
        }
        var serializedSelection = window.sessionStorage.getItem(PENDING_ITEM_KEY);
        if (!serializedSelection) {
            return null;
        }
        try {
            var pendingSelection = JSON.parse(serializedSelection);
            if (!(pendingSelection === null || pendingSelection === void 0 ? void 0 : pendingSelection.categoryId) || !(pendingSelection === null || pendingSelection === void 0 ? void 0 : pendingSelection.itemUrl) || !(pendingSelection === null || pendingSelection === void 0 ? void 0 : pendingSelection.title)) {
                window.sessionStorage.removeItem(PENDING_ITEM_KEY);
                return null;
            }
            return pendingSelection;
        }
        catch (_a) {
            window.sessionStorage.removeItem(PENDING_ITEM_KEY);
            return null;
        }
    };
    var clearPendingItemSelection = function () {
        if (typeof window !== "undefined") {
            window.sessionStorage.removeItem(PENDING_ITEM_KEY);
        }
    };
    var persistPendingItemSelection = function (selection) {
        if (typeof window !== "undefined") {
            window.sessionStorage.setItem(PENDING_ITEM_KEY, JSON.stringify(selection));
        }
    };
    var userFirstName = String((user === null || user === void 0 ? void 0 : user.first_name) || "").trim();
    var userLastName = String((user === null || user === void 0 ? void 0 : user.last_name) || "").trim();
    var userInitials = "".concat(userFirstName.charAt(0)).concat(userLastName.charAt(0)).trim().toUpperCase();
    var resolvedColorMode = (function () {
        if (typeof document !== "undefined") {
            var rootMode = document.documentElement.getAttribute("data-ui-color-mode");
            if (rootMode === "light" || rootMode === "dark") {
                return rootMode;
            }
        }
        if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
            return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        }
        return effectiveColorMode;
    })();
    var isDarkMode = resolvedColorMode === "dark";
    useEffect(function () {
        var _a;
        var sessionObject = window.session;
        if (!((_a = sessionObject === null || sessionObject === void 0 ? void 0 : sessionObject.categories) === null || _a === void 0 ? void 0 : _a.length)) {
            setSearchMap(new Map());
            setSearchEntities([]);
            setSearchIndexes({
                unigramIndex: new Map(),
                bigramIndex: new Map(),
                trigramIndex: new Map(),
            });
            return;
        }
        var nextSearchMap = new Map();
        var nextSearchEntities = [];
        var nextSearchIndexes = {
            unigramIndex: new Map(),
            bigramIndex: new Map(),
            trigramIndex: new Map(),
        };
        var addEntity = function (entityKey, entity) {
            var searchEntity = __assign(__assign({}, entity), { normalizedLabel: entity.label.toLowerCase() });
            nextSearchMap.set(entityKey, searchEntity);
            nextSearchEntities.push(searchEntity);
            indexLabelNgrams(searchEntity.normalizedLabel, nextSearchEntities.length - 1, nextSearchIndexes);
        };
        sessionObject.categories.forEach(function (category) {
            var _a;
            var categoryId = String(category.id);
            var normalizedCategoryLabel = String(category.name || "").trim();
            if (normalizedCategoryLabel) {
                addEntity("category:".concat(categoryId), {
                    id: categoryId,
                    type: "category",
                    categoryId: categoryId,
                    label: normalizedCategoryLabel,
                });
            }
            (_a = category.item_navigations) === null || _a === void 0 ? void 0 : _a.forEach(function (itemNavigation) {
                if (!shouldDisplayMenuEntity(itemNavigation)) {
                    return;
                }
                var itemNavigationId = String(itemNavigation.id);
                var itemLabel = String(itemNavigation.name || "").trim();
                if (!itemLabel) {
                    return;
                }
                addEntity("item:".concat(categoryId, ":").concat(itemNavigationId), {
                    id: itemNavigationId,
                    type: "item",
                    categoryId: categoryId,
                    itemNavigationId: itemNavigationId,
                    itemUrl: "".concat(window.relativeURI(), "/menu/categories/").concat(categoryId, "/items/").concat(itemNavigationId),
                    label: itemLabel,
                });
            });
        });
        setSearchMap(nextSearchMap);
        setSearchEntities(nextSearchEntities);
        setSearchIndexes(nextSearchIndexes);
    }, []);
    var matchingResults = useMemo(function () {
        var _a, _b, _c, _d;
        var normalizedSearchTerm = searchTerm.trim().toLowerCase();
        if (!normalizedSearchTerm || searchMap.size === 0) {
            return [];
        }
        var queryLength = normalizedSearchTerm.length;
        var candidateIds = [];
        if (queryLength >= 3) {
            var firstTrigram = normalizedSearchTerm.slice(0, 3);
            candidateIds = (_a = searchIndexes.trigramIndex.get(firstTrigram)) !== null && _a !== void 0 ? _a : [];
            for (var index = 1; index <= queryLength - 3; index += 1) {
                var trigram = normalizedSearchTerm.slice(index, index + 3);
                var trigramCandidates = (_b = searchIndexes.trigramIndex.get(trigram)) !== null && _b !== void 0 ? _b : [];
                if (candidateIds.length === 0 || trigramCandidates.length === 0) {
                    candidateIds = [];
                    break;
                }
                candidateIds = intersectSortedArrays(candidateIds, trigramCandidates);
                if (candidateIds.length === 0) {
                    break;
                }
            }
        }
        else if (queryLength === 2) {
            candidateIds = (_c = searchIndexes.bigramIndex.get(normalizedSearchTerm)) !== null && _c !== void 0 ? _c : [];
        }
        else {
            candidateIds = (_d = searchIndexes.unigramIndex.get(normalizedSearchTerm)) !== null && _d !== void 0 ? _d : [];
        }
        if (candidateIds.length === 0) {
            return [];
        }
        var results = [];
        for (var index = 0; index < candidateIds.length; index += 1) {
            var candidate = searchEntities[candidateIds[index]];
            if (candidate === null || candidate === void 0 ? void 0 : candidate.normalizedLabel.includes(normalizedSearchTerm)) {
                results.push(candidate);
                if (results.length >= MAX_SEARCH_RESULTS) {
                    break;
                }
            }
        }
        return results;
    }, [searchEntities, searchIndexes, searchMap, searchTerm]);
    useEffect(function () {
        var handleOutsideClick = function (event) {
            var _a, _b;
            if (isUserMenuOpen && !((_a = userMenuRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
                setIsUserMenuOpen(false);
            }
            if (isMobileSearchOpen && !((_b = mobileSearchRef.current) === null || _b === void 0 ? void 0 : _b.contains(event.target))) {
                setIsMobileSearchOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return function () {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isMobileSearchOpen, isUserMenuOpen]);
    useEffect(function () {
        if (!sticky) {
            return;
        }
        var root = document.documentElement;
        var updateStickyOffset = function () {
            var _a, _b;
            var headerHeight = (_b = (_a = headerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height) !== null && _b !== void 0 ? _b : 0;
            if (headerHeight > 0) {
                root.style.setProperty("--app-header-sticky-offset", "".concat(Math.round(headerHeight), "px"));
            }
        };
        updateStickyOffset();
        window.addEventListener("resize", updateStickyOffset);
        var resizeObserver = typeof ResizeObserver === "function"
            ? new ResizeObserver(updateStickyOffset)
            : null;
        if (resizeObserver && headerRef.current) {
            resizeObserver.observe(headerRef.current);
        }
        return function () {
            window.removeEventListener("resize", updateStickyOffset);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            root.style.removeProperty("--app-header-sticky-offset");
        };
    }, [sticky]);
    var navigateTo = function (path) {
        window.location.assign("".concat(window.relativeURI()).concat(path));
    };
    var navigateToCategory = function (categoryId) {
        var _a, _b;
        var categoryAnchor = "".concat(CATEGORY_HASH_PREFIX).concat(encodeURIComponent(categoryId));
        var menuHref = "".concat(window.relativeURI(), "/menu").concat(categoryAnchor);
        var isMenuPage = isMenuPagePath(window.location.pathname);
        if (!isMenuPage) {
            document.location.href = menuHref;
            return;
        }
        var categorySection = (_b = (_a = document.getElementById("category-".concat(categoryId))) !== null && _a !== void 0 ? _a : document.getElementById(categoryId)) !== null && _b !== void 0 ? _b : document.querySelector("[data-category-id=\"".concat(categoryId, "\"]"));
        if (!(categorySection instanceof HTMLElement)) {
            document.location.href = menuHref;
            return;
        }
        var header = document.querySelector(".radix-navbar--sticky");
        var headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
        var orderIntentNavbar = document.querySelector(".order-intent-navbar");
        var orderIntentHeight = orderIntentNavbar instanceof HTMLElement && orderIntentNavbar.getAttribute("aria-hidden") !== "true"
            ? orderIntentNavbar.getBoundingClientRect().height
            : 0;
        var categoryNav = document.querySelector(".category-navigation");
        var categoryNavHeight = categoryNav instanceof HTMLElement ? categoryNav.getBoundingClientRect().height : 0;
        var targetTop = window.scrollY + categorySection.getBoundingClientRect().top - headerHeight - orderIntentHeight - categoryNavHeight;
        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: "smooth",
        });
        history.replaceState(null, "", categoryAnchor);
    };
    var navigateToResult = function (searchResult) {
        if (searchResult.type === "category") {
            GoogleAnalytics.viewCategory(searchResult.label);
            navigateToCategory(searchResult.categoryId);
        }
        if (searchResult.type === "item" && searchResult.itemUrl) {
            GoogleAnalytics.viewItem({
                id: searchResult.itemNavigationId || searchResult.id,
                name: searchResult.label,
                category: searchResult.categoryId,
            });
            var pendingSelection_1 = {
                categoryId: searchResult.categoryId,
                itemUrl: searchResult.itemUrl,
                title: searchResult.label,
            };
            var isMenuPage = isMenuPagePath(window.location.pathname);
            if (!isMenuPage) {
                persistPendingItemSelection(pendingSelection_1);
                navigateToCategory(searchResult.categoryId);
            }
            else {
                navigateToCategory(searchResult.categoryId);
                window.setTimeout(function () {
                    setItemCustomizationTarget({
                        itemUrl: pendingSelection_1.itemUrl,
                        title: pendingSelection_1.title,
                    });
                }, 350);
            }
        }
        setSearchTerm("");
        setIsMobileSearchOpen(false);
    };
    useEffect(function () {
        if (!isMenuPagePath(window.location.pathname)) {
            return;
        }
        var pendingSelection = readPendingItemSelection();
        if (!pendingSelection) {
            return;
        }
        clearPendingItemSelection();
        navigateToCategory(pendingSelection.categoryId);
        window.setTimeout(function () {
            setItemCustomizationTarget({
                itemUrl: pendingSelection.itemUrl,
                title: pendingSelection.title,
            });
        }, 350);
    }, []);
    var renderSearchResults = function () {
        if (!searchTerm.trim()) {
            return null;
        }
        return (React.createElement("div", { className: "app-header-search-results", role: "listbox", "aria-label": "Search results" }, matchingResults.length > 0 ? matchingResults.map(function (searchResult) {
            var resultKey = "".concat(searchResult.type, ":").concat(searchResult.categoryId, ":").concat(searchResult.id);
            return (React.createElement("button", { key: resultKey, type: "button", className: "app-header-search-result", role: "option", onClick: function () { return navigateToResult(searchResult); } },
                React.createElement("span", { className: "app-header-search-result-label" }, searchResult.label),
                React.createElement("span", { className: "app-header-search-result-type" }, searchResult.type)));
        }) : (React.createElement("div", { className: "app-header-search-empty" }, "No menu categories or items found."))));
    };
    var handleSearchSubmit = function (event) {
        event.preventDefault();
        if (onSearchSubmit) {
            onSearchSubmit(searchTerm.trim());
        }
        if (matchingResults.length > 0) {
            navigateToResult(matchingResults[0]);
            return;
        }
        setIsMobileSearchOpen(false);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(RadixNavbar, { ref: headerRef, ariaLabel: "Main", sticky: sticky, stickyTop: stickyTop, layout: "brandActions", brand: React.createElement("a", { href: homeHref, className: "app-header-brand-link", "aria-label": brandLabel }, resolvedLogoSrc ? (React.createElement("img", { src: resolvedLogoSrc, alt: logoAlt || companyName, className: "app-header-logo" })) : (React.createElement("span", { className: "app-header-company-name" }, companyName))), actions: React.createElement(React.Fragment, null,
                React.createElement("div", { className: "app-header-search-wrap app-header-search-wrap--desktop" },
                    React.createElement("form", { className: "app-header-search app-header-search--desktop", onSubmit: handleSearchSubmit },
                        React.createElement(MagnifyingGlassIcon, { className: "app-header-search-icon", "aria-hidden": "true" }),
                        React.createElement(RadixInput, { type: "search", value: searchTerm, onChange: function (event) { return setSearchTerm(event.target.value); }, className: "app-header-search-input", placeholder: "Type / to search", "aria-label": "Search menu" })),
                    renderSearchResults()),
                React.createElement("div", { className: "app-header-mobile-search", ref: mobileSearchRef },
                    React.createElement(RadixButton, { className: "app-header-action", onClick: function () { return setIsMobileSearchOpen(function (isOpen) { return !isOpen; }); }, "aria-label": "Open search", "aria-expanded": isMobileSearchOpen, "aria-haspopup": "dialog" },
                        React.createElement(MagnifyingGlassIcon, null)),
                    isMobileSearchOpen ? (React.createElement("div", { className: "app-header-mobile-search-popover", role: "dialog", "aria-label": "Search" },
                        React.createElement("div", { className: "app-header-search-wrap" },
                            React.createElement("form", { className: "app-header-search", onSubmit: handleSearchSubmit },
                                React.createElement(MagnifyingGlassIcon, { className: "app-header-search-icon", "aria-hidden": "true" }),
                                React.createElement(RadixInput, { type: "search", value: searchTerm, onChange: function (event) { return setSearchTerm(event.target.value); }, className: "app-header-search-input", placeholder: "Search menu", "aria-label": "Search menu", autoFocus: true })),
                            renderSearchResults()))) : null),
                React.createElement(RadixButton, { className: "app-header-action", onClick: function (event) {
                        event.preventDefault();
                        openCart();
                        if (onCartClick) {
                            onCartClick(event);
                        }
                    }, "aria-label": "Cart".concat(cartLineCount > 0 ? ", ".concat(cartLineCount, " items") : "") },
                    React.createElement(CartIcon, null),
                    React.createElement("span", { className: "app-header-action-label" }, "Cart"),
                    (typeof cartCount === "number" ? cartCount : cartLineCount) > 0 ? (React.createElement("span", { className: "app-header-cart-badge", "aria-hidden": "true" }, cartLineCount)) : null),
                React.createElement("div", { className: "app-header-user-menu", ref: userMenuRef },
                    React.createElement(RadixButton, { className: "app-header-action", onClick: function () {
                            setIsUserMenuOpen(function (isOpen) { return !isOpen; });
                        }, "aria-label": isAuthenticated ? "My account menu" : "Sign in and sign up menu" }, isAuthenticated ? (React.createElement("span", { className: "app-header-user-initials", "aria-hidden": "true" }, userInitials || React.createElement(UserIcon, null))) : (React.createElement(UserIcon, null))),
                    isUserMenuOpen ? (React.createElement("div", { className: "app-header-user-menu-content", role: "menu", "aria-label": "Account options" }, isAuthenticated ? (React.createElement(React.Fragment, null,
                        React.createElement("button", { className: "app-header-user-menu-item", onClick: function () {
                                setIsUserMenuOpen(false);
                                navigateTo("/my-profile");
                            } },
                            React.createElement(PersonIcon, null),
                            "\u00A0\u00A0My Account"),
                        React.createElement("button", { className: "app-header-user-menu-item", onClick: function () {
                                setIsUserMenuOpen(false);
                                navigateTo("/logout");
                            } },
                            React.createElement(ExitIcon, null),
                            "\u00A0\u00A0Log out"),
                        React.createElement("button", { className: "app-header-user-menu-item", onClick: function () {
                                setIsUserMenuOpen(false);
                                toggleDarkMode();
                            } },
                            React.createElement(ThemeIcon, { isDarkMode: isDarkMode }),
                            "\u00A0\u00A0",
                            isDarkMode ? "Light mode" : "Dark mode"))) : (React.createElement(React.Fragment, null,
                        React.createElement("button", { className: "app-header-user-menu-item", onClick: function () {
                                setIsUserMenuOpen(false);
                                openSignInModal();
                            } },
                            React.createElement(PersonIcon, null),
                            "\u00A0\u00A0Sign in"),
                        React.createElement("button", { className: "app-header-user-menu-item", onClick: function () {
                                setIsUserMenuOpen(false);
                                openSignUpModal();
                            } },
                            React.createElement(PlusIcon, null),
                            "\u00A0\u00A0Sign up"),
                        React.createElement("button", { className: "app-header-user-menu-item", onClick: function () {
                                setIsUserMenuOpen(false);
                                toggleDarkMode();
                            } },
                            React.createElement(ThemeIcon, { isDarkMode: isDarkMode }),
                            "\u00A0\u00A0",
                            isDarkMode ? "Light mode" : "Dark mode"))))) : null)) }),
        React.createElement(ItemCustomizationDialog, { open: Boolean(itemCustomizationTarget), onOpenChange: function (nextOpen) {
                if (!nextOpen) {
                    setItemCustomizationTarget(null);
                }
            }, itemUrl: (itemCustomizationTarget === null || itemCustomizationTarget === void 0 ? void 0 : itemCustomizationTarget.itemUrl) || null, title: (itemCustomizationTarget === null || itemCustomizationTarget === void 0 ? void 0 : itemCustomizationTarget.title) || "Customize item", editMode: false })));
};
